import React, { useEffect, useState } from 'react';
import { api, formatApiErrorDetail } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';

const ROLES = [
  { value: 'front_desk', label: 'Front Desk' },
  { value: 'finance', label: 'Finance' },
  { value: 'manager', label: 'Manager' },
];

export default function Staff() {
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'front_desk' });
  const [busy, setBusy] = useState(false);

  const load = async () => setUsers((await api.get('/staff')).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/staff', form);
      toast.success('Staff member added');
      setCreating(false);
      setForm({ name: '', email: '', password: '', role: 'front_desk' });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const updateRole = async (u, role) => {
    try { await api.patch(`/staff/${u.id}`, { role }); await load(); toast.success('Role updated'); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/staff/${u.id}`, { active: !u.active }); await load(); toast.success(u.active ? 'Deactivated' : 'Reactivated'); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!users) return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Staff</h1>
          <p className="text-[#333]/70 mt-1">Front desk, finance, and managers.</p>
        </div>
        <button
          data-testid="staff-new-btn"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium"
        >
          <UserPlus size={16}/> New account
        </button>
      </div>

      <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#E8F5E9]/60 text-[#1F5A24]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#CFD8CF]" data-testid={`staff-row-${u.email}`}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`role-${u.email}`}
                    value={u.role}
                    onChange={(e) => updateRole(u, e.target.value)}
                    className="bg-white border border-[#CFD8CF] rounded-lg px-3 py-2"
                  >
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  {u.active ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#C8E6C9] text-[#1F5A24]">Active</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#EAEAEA] text-[#555]">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`toggle-${u.email}`}
                    onClick={() => toggleActive(u)}
                    className="px-3 py-2 rounded-lg border border-[#CFD8CF] hover:bg-[#F5F5F5]"
                  >
                    {u.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
          <form onSubmit={create} className="bg-[#FBFCFB] rounded-2xl border border-[#CFD8CF] w-full max-w-md p-6">
            <h3 className="font-heading text-xl text-[#1F5A24] mb-4">New staff account</h3>
            {['name', 'email', 'password'].map((k) => (
              <div key={k} className="mb-3">
                <label className="block text-sm font-medium mb-1 capitalize">{k}</label>
                <input
                  data-testid={`new-${k}`}
                  type={k === 'password' ? 'password' : k === 'email' ? 'email' : 'text'}
                  required
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full bg-white border border-[#CFD8CF] rounded-lg px-4 py-3"
                />
              </div>
            ))}
            <label className="block text-sm font-medium mb-1">Role</label>
            <select
              data-testid="new-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 mb-4"
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-3 rounded-lg border border-[#CFD8CF]">Cancel</button>
              <button data-testid="new-submit" disabled={busy} className="px-4 py-3 rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium disabled:opacity-60">
                {busy ? 'Creating…' : 'Create account'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
